/**
 * Phrase parts: the only shape a pilot brain may speak in. A brain returns typed parts, never a
 * finished string, so every number, runway and fix it says is rendered by the sim's own
 * phraseology helpers (`comms.ts`) and can be checked against the situation first. That is what
 * keeps a model from inventing an altitude or spelling a runway its own way.
 */
import type { AirportData } from '@atc/core';
import { spokenDigits } from '../airlines';
import type { Aircraft } from '../aircraft';
import { altitudeSpeech, altitudeText, callsignPhrase, fixSpeech, headingSpeech, headingText, runwaySpeech, speedSpeech, type Phrase } from '../comms';

export type PhrasePart =
  /** Plain words: letters and punctuation only, never digits. */
  | { t: 'words'; v: string }
  | { t: 'altitude'; ft: number }
  | { t: 'heading'; deg: number }
  | { t: 'speed'; kt: number }
  | { t: 'runway'; name: string }
  | { t: 'fix'; name: string }
  | { t: 'station'; which: 'app' | 'twr' | 'dep' }
  | { t: 'number'; n: number }
  | { t: 'callsign' };

/** Said letter by letter on the radio. */
const SPELLED: Record<string, string> = { ILS: 'I L S', RNP: 'R N P', VFR: 'V F R', IFR: 'I F R', RNAV: 'R NAV' };
/** Letters, spaces and light punctuation; never digits, so numbers can only come from typed parts. */
const WORDS_RE = /^[A-Za-z ,.'’-]+$/;
const MAX_PARTS = 24;
const MAX_WORDS = 90;

const speechWords = (v: string) => v.replace(/\b(ILS|RNP|VFR|IFR|RNAV)\b/g, (m) => SPELLED[m]!);

export interface RenderContext {
  ac: Aircraft;
  airport: AirportData;
  telephony?: Record<string, string>;
}

function renderPart(p: PhrasePart, ctx: RenderContext): Phrase {
  switch (p.t) {
    case 'words': return { text: p.v, speech: speechWords(p.v) };
    case 'altitude': return { text: altitudeText(p.ft), speech: altitudeSpeech(p.ft) };
    case 'heading': return { text: headingText(p.deg), speech: headingSpeech(p.deg) };
    case 'speed': return { text: `${p.kt} knots`, speech: speedSpeech(p.kt) };
    case 'runway': return { text: p.name, speech: runwaySpeech(p.name) };
    case 'fix': return { text: p.name, speech: fixSpeech(p.name) };
    case 'station': { const s = ctx.airport.radio[p.which]; return { text: s, speech: s }; }
    case 'number': return { text: String(p.n), speech: spokenDigits(p.n) };
    case 'callsign': return callsignPhrase(ctx.ac, ctx.telephony);
  }
}

/** Join parts into a radio phrase: one space between parts, tightened up before punctuation. */
export function renderParts(parts: readonly PhrasePart[], ctx: RenderContext): Phrase {
  const tidy = (s: string) => s.replace(/\s+([,.;:])/g, '$1').replace(/\s{2,}/g, ' ').trim();
  const rendered = parts.map((p) => renderPart(p, ctx));
  return { text: tidy(rendered.map((p) => p.text).join(' ')), speech: tidy(rendered.map((p) => p.speech).join(' ')) };
}

/** Values a brain is allowed to say for this aircraft, gathered by the sim from the situation. */
export interface AllowedValues {
  altitudesFt?: readonly number[];
  headingsDeg?: readonly number[];
  speedsKt?: readonly number[];
  runways?: readonly string[];
  fixes?: readonly string[];
  numbers?: readonly number[];
}

/**
 * Check parts before they are spoken: shape, length, and every value drawn from the situation.
 * Returns the reason it was rejected, or null. The rules brain always passes; a model brain that
 * fails falls back to the rules decision.
 */
export function checkParts(parts: readonly PhrasePart[], allowed: AllowedValues = {}): string | null {
  if (!Array.isArray(parts) || parts.length === 0) return 'no parts';
  if (parts.length > MAX_PARTS) return `too many parts (${parts.length})`;
  const has = (list: readonly number[] | readonly string[] | undefined, v: number | string) => !!list && (list as readonly unknown[]).includes(v);
  for (const p of parts) {
    switch (p?.t) {
      case 'words':
        if (typeof p.v !== 'string' || !WORDS_RE.test(p.v)) return `words must be letters only: "${p.v}"`;
        if (p.v.length > MAX_WORDS) return 'words too long';
        break;
      case 'altitude':
        if (!has(allowed.altitudesFt, p.ft)) return `altitude ${p.ft} is not in the situation`;
        break;
      case 'heading':
        if (!has(allowed.headingsDeg, p.deg)) return `heading ${p.deg} is not in the situation`;
        break;
      case 'speed':
        if (!has(allowed.speedsKt, p.kt)) return `speed ${p.kt} is not in the situation`;
        break;
      case 'runway':
        if (!has(allowed.runways, p.name)) return `runway ${p.name} is not in the situation`;
        break;
      case 'fix':
        if (!has(allowed.fixes, p.name)) return `fix ${p.name} is not in the situation`;
        break;
      case 'number':
        if (!has(allowed.numbers, p.n)) return `number ${p.n} is not in the situation`;
        break;
      case 'station': case 'callsign':
        break;
      default:
        return `unknown part ${JSON.stringify(p)}`;
    }
  }
  return null;
}
